/** @format */

import { useState, useEffect } from "react";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import toast from "react-hot-toast";

export const useOfflinePayments = (options = {}) => {
  const [offlinePayments, setOfflinePayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all offline payments
  const fetchOfflinePayments = async () => {
    try {
      setLoading(true);

      let q = query(
        collection(db, "OfflinePayment")
        // Note: orderBy("createdAt", "desc") removed to avoid errors with missing timestamps
      );

      // If filtering by status
      if (options.status) {
        q = query(q, where("status", "==", options.status));
      }

      // If filtering by user
      if (options.userId) {
        q = query(q, where("userId", "==", options.userId));
      }

      const querySnapshot = await getDocs(q);
      console.log("=== FIRESTORE OFFLINE PAYMENT FETCH ===");
      console.log("Total documents found:", querySnapshot.docs.length);

      const docs = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        console.log(`\n--- Document ${doc.id} ---`);
        console.log("Raw Firestore data:", data);
        console.log("Available fields:", Object.keys(data));

        // Check for amount fields
        if (data.paymentDetails)
          console.log("paymentDetails found:", data.paymentDetails);
        if (data.amount)
          console.log(
            "amount found:",
            data.amount,
            "Type:",
            typeof data.amount
          );
        if (data.total)
          console.log("total found:", data.total, "Type:", typeof data.total);

        // Check for product fields
        if (data.productDetails)
          console.log("productDetails found:", data.productDetails);
        if (data.productName)
          console.log("productName found:", data.productName);
        if (data.product) console.log("product found:", data.product);
        if (data.courseName) console.log("courseName found:", data.courseName);
        if (data.itemName) console.log("itemName found:", data.itemName);
        if (data.description)
          console.log("description found:", data.description);
        if (data.notes) console.log("notes found:", data.notes);

        // Ensure createdAt is properly set if missing
        if (!data.createdAt) {
          data.createdAt = new Date();
          console.warn(
            "Missing createdAt for payment:",
            doc.id,
            "Setting to current time"
          );
        }

        const processedDoc = {
          id: doc.id,
          ...data,
        };

        console.log("Processed document:", processedDoc);
        return processedDoc;
      });

      setOfflinePayments(docs);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error(`Error fetching offline payments: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Add new offline payment
  const addOfflinePayment = async (data) => {
    try {
      const paymentData = {
        ...data,
        status: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(
        collection(db, "OfflinePayment"),
        paymentData
      );
      toast.success("Offline payment submitted successfully");
      fetchOfflinePayments(); // Refresh the list
      return docRef.id;
    } catch (err) {
      toast.error(`Error submitting offline payment: ${err.message}`);
      throw err;
    }
  };

  // Approve offline payment and update user earnings
  const approveOfflinePayment = async (paymentId, adminUserId) => {
    try {
      // Get the payment details first
      const paymentRef = doc(db, "OfflinePayment", paymentId);
      const paymentDoc = await getDoc(paymentRef);

      if (!paymentDoc.exists()) {
        throw new Error("Payment not found");
      }

      const paymentData = paymentDoc.data();

      // Update payment status
      await updateDoc(paymentRef, {
        status: "approved",
        approvedBy: adminUserId,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Debug: Log the entire payment data to see actual structure
      console.log("Full payment data:", paymentData);

      // Update referredBy user's earnings (10% of total payment)
      if (paymentData.referredBy) {
        const userRef = doc(db, "users", paymentData.referredBy);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();

          // Initialize earnings field if it doesn't exist or is invalid
          let currentEarnings = userData.earnings;
          if (
            currentEarnings === undefined ||
            currentEarnings === null ||
            typeof currentEarnings !== "number" ||
            isNaN(currentEarnings)
          ) {
            currentEarnings = 0;
            console.log(
              "Initializing earnings field for user:",
              paymentData.referredBy
            );
          }

          // Try to get payment amount from different possible fields
          let paymentAmount = null;

          // Check for total field first (this is the main payment amount)
          if (paymentData.total) {
            paymentAmount = paymentData.total;
            console.log("Using total field:", paymentAmount);
          }
          // Check for paymentDetails.total as fallback
          else if (
            paymentData.paymentDetails &&
            paymentData.paymentDetails.total
          ) {
            paymentAmount = paymentData.paymentDetails.total;
            console.log("Using paymentDetails.total:", paymentAmount);
          }
          // Fallback to amount field
          else if (paymentData.amount) {
            paymentAmount = paymentData.amount;
            console.log("Using amount field:", paymentAmount);
          }
          // Check for other possible field names
          else if (paymentData.paymentAmount) {
            paymentAmount = paymentData.paymentAmount;
            console.log("Using paymentAmount field:", paymentAmount);
          }

          if (paymentAmount === null) {
            console.error(
              "No payment amount found in payment data:",
              paymentData
            );
            throw new Error("Payment amount not found in payment data");
          }

          // Validate payment amount
          if (
            typeof paymentAmount !== "number" ||
            isNaN(paymentAmount) ||
            paymentAmount <= 0
          ) {
            console.error(
              "Invalid payment amount:",
              paymentAmount,
              "Type:",
              typeof paymentAmount
            );
            throw new Error(`Invalid payment amount: ${paymentAmount}`);
          }

          const commissionAmount = paymentAmount * 0.1; // 10% commission

          const newEarnings = currentEarnings + commissionAmount;

          console.log("Earnings update details:", {
            userId: paymentData.referredBy,
            currentEarnings: currentEarnings,
            commissionAmount: commissionAmount,
            newEarnings: newEarnings,
            paymentAmount: paymentAmount,
          });

          await updateDoc(userRef, {
            earnings: newEarnings,
            updatedAt: serverTimestamp(),
          });

          toast.success(
            `Payment approved and ${
              paymentData.referredBy
            } earned $${commissionAmount.toFixed(2)}`
          );
        }
      }

      // Update user's courses and books based on purchased items
      if (paymentData.items && paymentData.items.length > 0) {
        console.log(
          "Updating user courses and books for purchased items:",
          paymentData.items
        );

        for (const item of paymentData.items) {
          if (item.type === "course") {
            // Add course to user's Courses subcollection
            try {
              const userCoursesRef = collection(
                db,
                "users",
                paymentData.userId,
                "Courses"
              );
              const courseRef = doc(userCoursesRef);

              await setDoc(courseRef, {
                course_id: item.id,
                course_name: item.name,
                enrolled_at: serverTimestamp(),
                status: "active",
                payment_id: paymentId,
                created_at: serverTimestamp(),
                updated_at: serverTimestamp(),
              });

              console.log(
                `Course ${item.name} added to user ${paymentData.userId}`
              );
            } catch (error) {
              console.error(`Error adding course ${item.name} to user:`, error);
            }
          } else if (item.type === "book") {
            // Add book to user's Mybooks subcollection
            try {
              const userBooksRef = collection(
                db,
                "Mybooks",
                paymentData.userId,
                "books"
              );
              const bookRef = doc(userBooksRef);

              await setDoc(bookRef, {
                bookid: item.id,
                book_name: item.name,
                assigned_at: serverTimestamp(),
                status: "active",
                payment_id: paymentId,
                created_at: serverTimestamp(),
                updated_at: serverTimestamp(),
              });

              console.log(
                `Book ${item.name} added to user ${paymentData.userId}`
              );
            } catch (error) {
              console.error(`Error adding book ${item.name} to user:`, error);
            }
          }
        }

        toast.success(
          `Payment approved and user access updated for ${paymentData.items.length} item(s)`
        );
      } else {
        toast.success("Payment approved successfully");
      }

      fetchOfflinePayments(); // Refresh the list
    } catch (err) {
      toast.error(`Error approving payment: ${err.message}`);
      throw err;
    }
  };

  // Reject offline payment
  const rejectOfflinePayment = async (paymentId, adminUserId, reason) => {
    try {
      const paymentRef = doc(db, "OfflinePayment", paymentId);
      await updateDoc(paymentRef, {
        status: "rejected",
        rejectedBy: adminUserId,
        rejectedAt: serverTimestamp(),
        rejectionReason: reason,
        updatedAt: serverTimestamp(),
      });

      toast.success("Payment rejected successfully");
      fetchOfflinePayments(); // Refresh the list
    } catch (err) {
      toast.error(`Error rejecting payment: ${err.message}`);
      throw err;
    }
  };

  // Delete offline payment
  const deleteOfflinePayment = async (paymentId) => {
    try {
      await deleteDoc(doc(db, "OfflinePayment", paymentId));
      toast.success("Payment deleted successfully");
      fetchOfflinePayments(); // Refresh the list
    } catch (err) {
      toast.error(`Error deleting payment: ${err.message}`);
      throw err;
    }
  };

  // Get single offline payment
  const getOfflinePayment = async (paymentId) => {
    try {
      const paymentDoc = await getDoc(doc(db, "OfflinePayment", paymentId));
      if (paymentDoc.exists()) {
        return { id: paymentDoc.id, ...paymentDoc.data() };
      }
      return null;
    } catch (err) {
      toast.error(`Error fetching payment: ${err.message}`);
      throw err;
    }
  };

  // Update offline payment
  const updateOfflinePayment = async (paymentId, data) => {
    try {
      const paymentRef = doc(db, "OfflinePayment", paymentId);
      await updateDoc(paymentRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });

      toast.success("Payment updated successfully");
      fetchOfflinePayments(); // Refresh the list
    } catch (err) {
      toast.error(`Error updating payment: ${err.message}`);
      throw err;
    }
  };

  // Real-time listener for offline payments
  useEffect(() => {
    if (options.realtime) {
      let q = query(
        collection(db, "OfflinePayment")
        // Note: orderBy("createdAt", "desc") removed to avoid errors with missing timestamps
      );

      if (options.status) {
        q = query(q, where("status", "==", options.status));
      }

      if (options.userId) {
        q = query(q, where("userId", "==", options.userId));
      }

      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          const docs = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            // Ensure createdAt is properly set if missing
            if (!data.createdAt) {
              data.createdAt = new Date();
              console.warn(
                "Missing createdAt for payment:",
                doc.id,
                "Setting to current time"
              );
            }
            return {
              id: doc.id,
              ...data,
            };
          });
          setOfflinePayments(docs);
          setLoading(false);
        },
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } else {
      fetchOfflinePayments();
    }
  }, [options.realtime, options.status, options.userId]);

  return {
    offlinePayments,
    loading,
    error,
    fetchOfflinePayments,
    addOfflinePayment,
    approveOfflinePayment,
    rejectOfflinePayment,
    deleteOfflinePayment,
    getOfflinePayment,
    updateOfflinePayment,
  };
};
